import React, { useState } from 'react'
import {
  Search,
  ChevronDown,
  Camera,
  Zap,
  PackageX,
  SlidersHorizontal,
  LayoutGrid,
  Rows3
} from 'lucide-react'
import { Button } from '../ui/Button'
import { useLanguage } from '../../contexts/LanguageContext'
import { Toolbar, ToolbarGroup } from './ui/toolbar'
import type { ProductColor, ProductSize } from '../../utils/productGrouping'

interface InhouseCommandToolbarProps {
  searchQuery: string
  onSearchChange: (value: string) => void
  filterCategory: string
  onFilterCategoryChange: (value: string) => void
  filterColor: string
  onFilterColorChange: (value: string) => void
  filterSize: string
  onFilterSizeChange: (value: string) => void
  categories: string[]
  colors: ProductColor[]
  sizes: ProductSize[]
  showOutOfStock: boolean
  onShowOutOfStockChange: (value: boolean) => void
  onOpenCamera: () => void
  onOpenExternalScanner: () => void
  onClearFilters: () => void
  hasActiveFilters: boolean
  browseView: 'grouped' | 'all'
  onBrowseViewChange: (view: 'grouped' | 'all') => void
}

export type BrowseViewMode = 'grouped' | 'all'

export function InhouseCommandToolbar({
  searchQuery,
  onSearchChange,
  filterCategory,
  onFilterCategoryChange,
  filterColor,
  onFilterColorChange,
  filterSize,
  onFilterSizeChange,
  categories,
  colors,
  sizes,
  showOutOfStock,
  onShowOutOfStockChange,
  onOpenCamera,
  onOpenExternalScanner,
  onClearFilters,
  hasActiveFilters,
  browseView,
  onBrowseViewChange
}: InhouseCommandToolbarProps) {
  const { t } = useLanguage()
  const [filtersOpen, setFiltersOpen] = useState(false)

  const activeFilterCount = [
    filterCategory,
    filterColor,
    filterSize
  ].filter(Boolean).length

  return (
    <div className="space-y-2 mb-2">
      <Toolbar className="gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
          <input
            type="search"
            placeholder="Search name, SKU, reference..."
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>

        <ToolbarGroup>
          <Button variant="secondary" onClick={onOpenCamera} title="Camera Scanner">
            <Camera className="h-4 w-4" />
          </Button>
          <Button variant="secondary" onClick={onOpenExternalScanner} title="External Scanner">
            <Zap className="h-4 w-4" />
          </Button>
        </ToolbarGroup>

        <button
          type="button"
          onClick={() => setFiltersOpen(prev => !prev)}
          aria-expanded={filtersOpen}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
            filtersOpen || activeFilterCount > 0
              ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-300'
              : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300'
          }`}
        >
          <SlidersHorizontal className="h-4 w-4" />
          <span className="hidden sm:inline">Filters</span>
          {activeFilterCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 text-xs font-bold rounded-full bg-teal-600 text-white">
              {activeFilterCount}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => onBrowseViewChange(browseView === 'grouped' ? 'all' : 'grouped')}
          aria-pressed={browseView === 'all'}
          title={browseView === 'grouped' ? t('inhouse.allProductsView') : t('inhouse.groupedView')}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
            browseView === 'all'
              ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-300'
              : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300'
          }`}
        >
          {browseView === 'grouped' ? (
            <LayoutGrid className="h-4 w-4" />
          ) : (
            <Rows3 className="h-4 w-4" />
          )}
          <span className="hidden sm:inline">
            {browseView === 'grouped' ? t('inhouse.groupedView') : t('inhouse.allProductsView')}
          </span>
        </button>

        <button
          type="button"
          onClick={() => onShowOutOfStockChange(!showOutOfStock)}
          aria-pressed={showOutOfStock}
          title={t('inhouse.showOutOfStock')}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
            showOutOfStock
              ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700 text-red-700 dark:text-red-300'
              : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300'
          }`}
        >
          <PackageX className="h-4 w-4" />
          <span className="hidden lg:inline">{t('inhouse.showOutOfStock')}</span>
        </button>
      </Toolbar>

      {filtersOpen && (
        <Toolbar className="gap-2 pt-1 pb-2 border-t border-gray-100 dark:border-gray-700/50">
          <div className="relative flex-1 min-w-[120px] max-w-[180px]">
            <select
              value={filterCategory}
              onChange={e => onFilterCategoryChange(e.target.value)}
              className="w-full appearance-none pl-3 pr-8 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 cursor-pointer"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>

          <div className="relative flex-1 min-w-[120px] max-w-[160px]">
            <select
              value={filterColor}
              onChange={e => onFilterColorChange(e.target.value)}
              className="w-full appearance-none pl-3 pr-8 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 cursor-pointer"
            >
              <option value="">All Colors</option>
              {colors.map(color => (
                <option key={color.id} value={color.id}>{color.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>

          <div className="relative flex-1 min-w-[120px] max-w-[140px]">
            <select
              value={filterSize}
              onChange={e => onFilterSizeChange(e.target.value)}
              className="w-full appearance-none pl-3 pr-8 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 cursor-pointer"
            >
              <option value="">All Sizes</option>
              {sizes.map(size => (
                <option key={size.id} value={size.id}>{size.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>

          {hasActiveFilters && (
            <button
              type="button"
              onClick={onClearFilters}
              className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              Clear
            </button>
          )}
        </Toolbar>
      )}
    </div>
  )
}
